/**
 * API configuration for the Synapse Workers backend.
 *
 * Resolution order:
 * 1. EXPO_PUBLIC_API_URL — baked into the web build per environment by the deploy
 *    workflows (staging build -> staging-api; prod build -> api). webpack.config.js
 *    inlines it with DefinePlugin; Expo's webpack config does not do so on SDK 41+.
 * 2. Hostname fallback — for builds with no env var set.
 */

export const API_URLS = {
  development: "http://localhost:8787",
  staging: "https://staging-api.synapse.gbe.games",
  production: "https://api.synapse.gbe.games",
} as const;

export type ApiEnvironment = keyof typeof API_URLS;

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Map a page hostname to an API environment.
 *
 * - localhost -> development
 * - any host containing "staging" -> staging
 * - Cloudflare Pages branch/preview aliases (`<branch>.<project>.pages.dev`,
 *   e.g. develop.synapse-6p8.pages.dev) -> staging
 * - everything else (synapse.gbe.games, the bare `<project>.pages.dev`) -> production
 */
export function getEnvironmentForHostname(
  hostname: string | undefined | null,
): ApiEnvironment {
  const host = (hostname ?? "").toLowerCase();
  if (LOCAL_HOSTS.has(host)) return "development";
  if (host.includes("staging")) return "staging";
  if (host.endsWith(".pages.dev") && host.split(".").length > 3) {
    return "staging";
  }
  return "production";
}

export function resolveApiBaseUrl(options: {
  envUrl?: string;
  isDev: boolean;
  hostname?: string | null;
}): string {
  if (options.envUrl) return options.envUrl;
  if (options.isDev) return API_URLS.development;
  return API_URLS[getEnvironmentForHostname(options.hostname)];
}

export const API_BASE_URL = resolveApiBaseUrl({
  envUrl: process.env.EXPO_PUBLIC_API_URL,
  isDev: __DEV__,
  hostname:
    typeof window !== "undefined" ? window.location?.hostname : undefined,
});
